import api from './api';
import { authClient } from './browserApi';
import { clearAuthSession, markAuthSessionLoading, markAuthSessionError, syncAuthSessionFromPayload } from './authSessionStore';
import { beginLogout, finishLogout, isLogoutPending, getAuthEpoch, setSessionLoader, clearLegacyTokens } from './browserAuthRuntime';

const FORCE_LOGIN_KEY = 'forceLogin';
let activeValidation;
let validatedEpoch = -1;
let validatedAt = 0;
let pendingLogout;

export async function logoutUser() {
  if (pendingLogout) return pendingLogout;
  beginLogout();
  validatedAt = 0;
  clearLegacyTokens();
  pendingLogout = authClient.post('/auth/v2/logout').then(() => {
    finishLogout(true);
    return true;
  }).catch(() => {
    finishLogout(false);
    return false;
  }).finally(() => { pendingLogout = null; });
  return pendingLogout;
}

export async function ensureActiveSession({ force = false } = {}) {
  if (isLogoutPending()) {
    const ended = await logoutUser();
    if (!ended) {
      const error = new Error('Não foi possível encerrar a sessão. Verifique sua conexão e tente novamente.');
      markAuthSessionError(error);
      throw error;
    }
    clearAuthSession();
    return false;
  }
  const epoch = getAuthEpoch();
  if (!force && validatedEpoch === epoch && Date.now() - validatedAt < 15000) return true;
  if (activeValidation?.epoch === epoch) return activeValidation.promise;
  markAuthSessionLoading();
  const promise = api.get('/auth/v2/session').then((response) => {
    if (getAuthEpoch() !== epoch) return false;
    if (!response.data?.session) throw new Error('Resposta de sessão inválida');
    validatedEpoch = epoch;
    validatedAt = Date.now();
    return syncAuthSessionFromPayload(response.data);
  }).catch((error) => {
    if (getAuthEpoch() !== epoch) return false;
    validatedAt = 0;
    if (error.response?.data?.code === 'AUTH_SESSION_INVALID') { clearAuthSession(); return false; }
    if (error.code !== 'ERR_CANCELED') markAuthSessionError(error);
    throw error;
  }).finally(() => { if (activeValidation?.promise === promise) activeValidation = null; });
  activeValidation = { epoch, promise };
  return promise;
}
setSessionLoader(() => ensureActiveSession({ force: true }));

export function markForceLogin() {
  if (typeof window === 'undefined') return;
  window.localStorage?.setItem(FORCE_LOGIN_KEY, '1');
  window.sessionStorage?.setItem(FORCE_LOGIN_KEY, '1');
}

export function consumeForceLogin() {
  if (typeof window === 'undefined') return false;
  const value =
    window.sessionStorage?.getItem(FORCE_LOGIN_KEY) ??
    window.localStorage?.getItem(FORCE_LOGIN_KEY);
  if (!value) return false;
  window.sessionStorage?.removeItem(FORCE_LOGIN_KEY);
  window.localStorage?.removeItem(FORCE_LOGIN_KEY);
  return true;
}
